import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { ASSETS, APP_STORE_URL } from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";

const { wordmark: WORDMARK } = ASSETS;

const NAV_LINKS = [
  { label: "Features", href: "/#features" },
  { label: "Use Cases", href: "/#use-cases" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Share", href: "/share" },
  { label: "Links", href: "/go" },
  { label: "Disclaimer", href: "/#faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const blur = useTransform(scrollY, [0, 80], [6, 18]);
  const bgOpacity = useTransform(scrollY, [0, 80], [0.4, 0.75]);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const u = scrollY.on("change", (v) => setScrolled(v > 12));
    return () => u();
  }, [scrollY]);

  return (
    <motion.header
      data-testid="site-navbar"
      style={{
        backdropFilter: scrolled ? `blur(18px)` : `blur(6px)`,
        backgroundColor: scrolled ? "rgba(5,5,5,0.78)" : "rgba(5,5,5,0.35)",
      }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          aria-label="Stable-ish home"
          className="flex items-center gap-3 group"
        >
          <div className="relative overflow-hidden h-10 w-[180px] sm:w-[200px] select-none">
            <img
              src={WORDMARK}
              alt="Stable-ish"
              draggable="false"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-w-none pointer-events-none"
              style={{ height: "260px" }}
            />
          </div>
          <span className="hidden lg:inline-flex font-mono text-[9px] uppercase tracking-[0.25em] text-white/45 border-l border-white/15 pl-3 ml-1">
            v1.1.5 · live on iOS
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/65 hover:text-white transition-colors whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-7">
          <a
            href="/subscribe"
            data-testid="nav-subscribe-link"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/65 hover:text-[#FF2A2A] transition-colors whitespace-nowrap"
          >
            Stay in the loop
          </a>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("download_click", "navbar", { store: "apple" })}
            data-testid="nav-cta"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-white/90 text-black font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
          >
            Download
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden p-2 border border-white/10"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <List size={18} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/95 px-5 py-6 space-y-5">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block font-mono text-xs uppercase tracking-[0.2em] text-white/80"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/subscribe"
            onClick={() => setOpen(false)}
            data-testid="nav-subscribe-link-mobile"
            className="block font-mono text-xs uppercase tracking-[0.2em] text-white/80"
          >
            Stay in the loop
          </a>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent("download_click", "navbar-mobile", { store: "apple" });
              setOpen(false);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black font-mono text-[11px] uppercase tracking-[0.2em]"
          >
            Download on iOS
          </a>
        </div>
      )}
    </motion.header>
  );
}
